/**
 * UploadStep — Step 1 of the New Import wizard (FR-03.1 / FR-03.2).
 *
 * Drag-and-drop + click-to-browse zone accepting `.csv` / `.json`. Dragover
 * highlights the zone. The "-- OR --" + "Sent from NUCCE Extension" affordance
 * represents the Baby Chops auto-import path: it folds into the same
 * upload→complete handshake with `source='baby_chops'` (no separate endpoint).
 *
 * The format hint names the ChopLink key columns (2026-09-28). It is the
 * answer to "tell people what's going to work and not work when they upload a
 * file": the gate at `/complete` refuses a header that is not ChopLink's, and
 * this sentence is what keeps most such files from being picked at all.
 *
 * Tokenized: token color utilities + `text-*` roles + Tailwind's 4px scale.
 * No inline styles, no arbitrary `text-[Npx]`.
 */

import { FileUp, Inbox } from "lucide-react";
import { type ChangeEvent, type DragEvent, useRef, useState } from "react";

const ACCEPTED_EXTENSIONS = [".csv", ".json"];

function isAccepted(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export interface UploadStepProps {
  /** Called with the chosen file + whether it came via the NUCCE path. */
  onFileSelected: (file: File, fromNucce: boolean) => void;
  /** Surfaced when a rejected (wrong-type) file is dropped/picked. */
  error: string | null;
  onError: (message: string | null) => void;
}

export function UploadStep({ onFileSelected, error, onError }: UploadStepProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function handleFiles(files: FileList | null, fromNucce: boolean): void {
    const file = files?.[0];
    if (!file) return;
    if (!isAccepted(file)) {
      onError("Unsupported file type. Upload a .csv or .json file.");
      return;
    }
    onError(null);
    onFileSelected(file, fromNucce);
  }

  function onDrop(e: DragEvent<HTMLButtonElement>): void {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files, false);
  }

  function onChange(e: ChangeEvent<HTMLInputElement>): void {
    handleFiles(e.target.files, false);
  }

  return (
    <div data-testid="upload-step" className="flex flex-col gap-4">
      <button
        type="button"
        data-testid="upload-zone"
        aria-label="Upload a CSV or JSON file"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={[
          "flex w-full flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed p-12 text-center transition-colors cursor-pointer",
          dragging ? "border-accent bg-accent-muted" : "border-border hover:border-accent",
        ].join(" ")}
      >
        <FileUp className="w-8 h-8 text-accent" aria-hidden="true" />
        <span className="text-body text-text">Drag and drop a file here, or click to browse</span>
        <span data-testid="upload-format-hint" className="text-caption text-text-secondary">
          ChopLink exports only (.csv, .json), with the columns First Name, Last Name, LinkedIn URL,
          Job 1 Title, Job 1 Company. Other files are refused.
        </span>
      </button>

      {/* The file input lives OUTSIDE the button (an input inside a button is
          invalid HTML); both the zone and the NUCCE button trigger it. */}
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.json,text/csv,application/json"
        className="hidden"
        onChange={onChange}
        data-testid="file-input"
      />

      <div className="flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-border" />
        <span className="text-caption text-text-secondary">-- OR --</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <button
        type="button"
        data-testid="nucce-source"
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-4 py-3 text-label font-semibold text-text-secondary transition-colors hover:border-accent hover:text-accent"
      >
        <Inbox className="w-4 h-4" aria-hidden="true" />
        Sent from NUCCE Extension
      </button>

      {error ? (
        <p data-testid="upload-error" className="text-caption text-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
